import React, { useState } from 'react';
import { PRIORITY_ORDER, type BrokerPriorityCounts } from './brokerViewTokens';
import type { ClientViewVinRow, BrokerSliceVinRow, BrokerChipFilterParams } from '../../api/types';
import { BrokerVinTable, type MatchType } from './BrokerVinTable';
import { SeverityBar } from './SeverityBar';
import type { SliceBoardParams } from './BrokerSliceView';

/** Minimal seller shape the slim broker card needs (from the marker aggregate). */
export interface SellerCardData {
  seller_id: string;
  seller_name: string;
  total_vehicles: number;
  priority_counts: BrokerPriorityCounts;
}

/** Per-seller extras resolved lazily for the visible carousel page. */
export interface BrokerSellerMeta {
  salesforceId: string | null;
  /** Days since an RDS rep last logged a Salesforce activity against this seller. */
  daysSinceLastContact: number | null;
  lastContactDate: string | null;
  contactCount30d: number;
  /** The rep who logged that activity, so the broker knows who to coordinate with. */
  lastContactAgent: string | null;
}

/** Contact recency reads as a traffic light: touched this week / this month / stale.
 * Coverage is ~59% of clients, so "no logged contact" is not "never contacted". */
function contactTone(days: number | null): { fg: string; bg: string } {
  if (days == null) return { fg: '#64748b', bg: '#f1f5f9' };
  if (days <= 7) return { fg: '#15803d', bg: '#dcfce7' };
  if (days <= 30) return { fg: '#a16207', bg: '#fef3c7' };
  return { fg: '#b91c1c', bg: '#fee2e2' };
}

interface DealerCardProps {
  dealer: SellerCardData;
  /** Salesforce id + RDS contact recency (lazily fetched for the visible page). */
  meta?: BrokerSellerMeta | null;
  selected: boolean;
  expanded: boolean;
  onClick: () => void;
  onVinClick?: (vin: ClientViewVinRow) => void;
  /** Board-scope filters passed to the shared VIN table. */
  sliceBoardParams?: SliceBoardParams;
  /** Match-type filter applied to the seller's VIN table. */
  matchTypes?: MatchType[];
  /** Shared chip filters (urgency / days / listing / bids / price), server-side. */
  chipFilters?: BrokerChipFilterParams;
  /** Stage a VIN under its best matched buyer (basket). */
  onAddToBasket?: (v: BrokerSliceVinRow, topN: number) => void;
  /** Composite "<buyer_uid>::<vin>" basket keys (for the row's added state). */
  basketVins?: Set<string>;
  /** Undo: clicking a staged row's ✓ removes that VIN from the basket. */
  onRemoveFromBasket?: (vin: string) => void;
}

/** Compact stacked severity bar + legend (broker's only per-seller signal).
 * Extracted to ./SeverityBar and shared across all three slice cards. */

export const BrokerViewDealerCard: React.FC<DealerCardProps> = ({ dealer, meta, selected, expanded, onClick, onVinClick, sliceBoardParams, matchTypes, chipFilters, onAddToBasket, basketVins, onRemoveFromBasket }) => {
  const [copiedSeller, setCopiedSeller] = useState(false);
  const salesforceId = meta?.salesforceId ?? null;
  const days = meta?.daysSinceLastContact ?? null;
  const tone = contactTone(days);
  const agent = meta?.lastContactAgent?.trim() || null;
  const contactLabel = days == null
    ? 'No logged contact'
    : days === 0 ? 'Contacted today' : `Contacted ${days}d ago`;
  // Name the rep when we have one: "an RDS rep" left the broker with no idea who
  // is already on the account.
  const contactTitle = days == null
    ? 'No RDS Salesforce activity logged for this seller. Coverage is partial (~59% of clients), so this is not proof they were never contacted.'
    : `${agent ?? 'An RDS rep'} last logged a Salesforce activity ${days} day${days === 1 ? '' : 's'} ago`
      + (meta?.lastContactDate ? ` (${meta.lastContactDate})` : '')
      + `. ${meta?.contactCount30d ?? 0} contact${(meta?.contactCount30d ?? 0) === 1 ? '' : 's'} in the last 30 days.`;

  const total = PRIORITY_ORDER.reduce(
    (sum, k) => sum + (dealer.priority_counts?.[k] || 0),
    0,
  );

  const copySellerId = () => {
    // Right-click on the card head used to differ by platform: Mac selected the
    // text, Windows fell through and expanded the card (§6.6).
    if (!navigator.clipboard?.writeText) {
      console.error('Clipboard unavailable; seller number not copied');
      return;
    }
    navigator.clipboard.writeText(dealer.seller_id).then(() => {
      setCopiedSeller(true);
      setTimeout(() => setCopiedSeller(false), 1500);
    }).catch((err) => console.error('Failed to copy seller number', err));
  };

  return (
    <div
      className={`bv-card ${selected ? 'selected' : ''} ${expanded ? 'expanded' : ''}`}
      data-usage-seller-uid={dealer.seller_id}
      data-usage-section="seller_card"
    >
      <div className="bv-card-top">
        <button
          type="button"
          className="bv-card-headbtn"
          onClick={onClick}
          onContextMenu={(e) => { e.preventDefault(); copySellerId(); }}
          title="Click to expand · right-click to copy the seller number"
          aria-expanded={expanded}
        >
          <span className="bv-card-expand-caret" aria-hidden="true">{expanded ? '▾' : '▸'}</span>
          <span className="bv-card-title">
            <span className="bv-card-name">{dealer.seller_name || dealer.seller_id}</span>
            <span className="bv-card-sid">· {dealer.seller_id}</span>
          </span>
          <span className="bv-card-count">{dealer.total_vehicles} VIN{dealer.total_vehicles === 1 ? '' : 's'}</span>
        </button>
        <div className="bv-card-links">
          {meta && (
            <span
              className="bv-card-contact"
              style={{ color: tone.fg, background: tone.bg }}
              title={contactTitle}
            >
              {contactLabel}
              {agent && <span className="bv-card-contact-agent"> · {agent}</span>}
            </span>
          )}
          <button
            type="button"
            className="bv-card-link bv-card-copyid"
            onClick={copySellerId}
            title={`Copy seller # ${dealer.seller_id}`}
            aria-label={`Copy seller number ${dealer.seller_id}`}
          >
            {copiedSeller ? 'Copied!' : '⧉ Copy #'}
          </button>
          <a
            className="bv-card-link"
            href="https://sellcenter.manheim.com/admin"
            target="_blank"
            rel="noopener noreferrer"
            title={`Open Manheim Selling Center (copies seller # ${dealer.seller_id} to clipboard)`}
            onClick={copySellerId}
          >
            Selling Center ↗
          </a>
          {salesforceId && (
            <a
              className="bv-card-link"
              href={`https://manheim.lightning.force.com/lightning/r/Account/${salesforceId}/view`}
              target="_blank"
              rel="noopener noreferrer"
              title="Open client profile in Salesforce"
            >
              Salesforce ↗
            </a>
          )}
        </div>
      </div>

      <button
        type="button"
        className="bv-card-sev"
        onClick={onClick}
        aria-expanded={expanded}
        aria-label={`${total} VINs by urgency`}
      >
        <SeverityBar counts={dealer.priority_counts} total={total} />
      </button>

      {expanded && (
        <div className="bv-card-drawer">
          <BrokerVinTable
            slice="seller"
            slotKey={dealer.seller_id}
            boardParams={sliceBoardParams ?? {}}
            matchTypes={matchTypes}
            chipFilters={chipFilters}
            showBuyer
            onVinClick={onVinClick}
            onAddToBasket={onAddToBasket}
            basketVins={basketVins}
            onRemoveFromBasket={onRemoveFromBasket}
          />
        </div>
      )}
    </div>
  );
};
